import { useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { add } from 'date-fns';
import { getDateInTFormat } from '../domains';
import { getLogContentAction, setLogDateTime } from '../../reducers/logsSlice';
import { getSearchParams, updateSearchParams } from '../utils';
import { Nullable } from '../types';

interface UseLogContentRequestProps {
  contentFirstLogDate?: Nullable<string>;
  contentSecondLogDate?: Nullable<string>;
  selectedLogStartTime: Nullable<string>;
  farmTimeZone: string;
  activeLogNode: Nullable<string>;
  useEndDate: boolean;
  startParamKey?: string;
  endParamKey?: string;
  singleDateParamKey?: string;
}

const useLogContentRequest = ({
  contentFirstLogDate,
  contentSecondLogDate,
  selectedLogStartTime,
  farmTimeZone,
  activeLogNode,
  useEndDate,
  startParamKey = 'startDate',
  endParamKey = 'endDate',
  singleDateParamKey = 'date',
}: UseLogContentRequestProps) => {
  const dispatch = useDispatch();
  const { externalId: entityId } = useParams<{ externalId: string }>();
  const requestMoreContent = useCallback(
    (preceding: boolean) => {
      if (!entityId || !selectedLogStartTime) return;

      const delta = preceding ? -1 : 1;

      const startParam = getSearchParams(startParamKey);
      const endParam = getSearchParams(endParamKey);
      const singleDateParam = getSearchParams(singleDateParamKey);

      let newStart: Date;
      let newEnd: Date;

      if (useEndDate && startParam && endParam) {
        const originalStart = new Date(startParam);
        const originalEnd = new Date(endParam);

        newStart = add(originalStart, { hours: delta });
        newEnd = add(originalEnd, { hours: delta });

        dispatch(
          setLogDateTime({
            startDate: newStart.toString(),
            endDate: newEnd.toString(),
          }),
        );
        updateSearchParams(startParamKey, getDateInTFormat(newStart), true);
        updateSearchParams(endParamKey, getDateInTFormat(newEnd), true);
        dispatch(
          getLogContentAction({
            entityId,
            startDate: newStart.toString(),
            logFileName: activeLogNode || '',
            preceding,
          }),
        );
      } else if (singleDateParam) {
        const originalDate = new Date(singleDateParam);
        newStart = add(originalDate, { hours: delta });

        dispatch(
          setLogDateTime({
            startDate: newStart.toString(),
            endDate: newStart.toString(),
          }),
        );
        updateSearchParams(
          singleDateParamKey,
          getDateInTFormat(newStart),
          true,
        );
        dispatch(
          getLogContentAction({
            entityId,
            startDate: newStart.toString(),
            logFileName: activeLogNode || '',
            preceding,
          }),
        );
      }
    },
    [
      entityId,
      selectedLogStartTime,
      contentFirstLogDate,
      contentSecondLogDate,
      farmTimeZone,
      activeLogNode,
      useEndDate,
      startParamKey,
      endParamKey,
      singleDateParamKey,
    ],
  );

  return { requestMoreContent };
};

export default useLogContentRequest;
